<template>
  <CustomModal ref="modal" class="business-management-modal">
    <div class="document-workspace">
      <!-- Onglets de classeur au sommet de la modale : tout ce qui est dessous concerne le document sélectionné -->
      <DocumentsNavigation v-if="business" :business="business" v-model:selected-document="selectedDocument" />
      <div class="workspace-body">
      <!-- key : à chaque ouverture (autre contrat, rechargement) tout le formulaire est recréé, jamais comparé à celui du contrat précédent
           (sinon Vue compare des enfants dont le nombre d'avoirs diffère et provoque « insertBefore of null ») -->
      <CustomForm ref="form" :key="documentsKey" :inputs=inputs :submit=buttons.submit v-if="business">
        <div class="workspace-grid">

          <!-- ============ COLONNE GAUCHE : navigation entre documents + génération + situation financière ============ -->
          <aside class="documents-sidebar">
            <div class="download-container">
              <button type="button" class="download-button" :disabled="downloadButton.disabled" @click="downloadSelectedDocument">
                <font-awesome-icon :icon="isDownloading ? 'lemon' : 'download'" :spin="isDownloading" aria-hidden="true" />
                <span>{{ downloadButton.text }}</span>
              </button>
            </div>

            <template v-if="!readOnly">
              <div class="sidebar-divider"></div>

              <SidebarAccordion title="Générer">
                <div class="generate-buttons-container">
                  <CustomForm ref="generateInvoiceDocumentForm" :inputs=[] :submit=buttons.generateInvoiceDocument>
                    <CustomButton type="submit" @click="generateInvoiceDocument" :is-secondary="true" :isCompact="true" :params=buttons.generateInvoiceDocument />
                  </CustomForm>
                  <CustomForm ref="generateDepositDocumentForm" :inputs=[] :submit=buttons.generateDepositDocument>
                    <CustomButton type="submit" @click="generateDepositDocument" :is-secondary="true" :isCompact="true" :params=buttons.generateDepositDocument />
                  </CustomForm>
                  <CustomButton @click="$refs.addCreditNoteModal.openModal(business)" :is-secondary="true" :isCompact="true" :params=buttons.createCreditNote />
                </div>
              </SidebarAccordion>
            </template>

            <template v-if="!readOnly">
              <div class="actions-divider"></div>
              <OrderNumberSection :business="business" :order-number-input="inputs.orderNumber" :read-only="readOnly" :callback="closeChildModalCallback"/>
            </template>

            <FinancialSummarySection :key="documentsKey + '-financial'" :business="business" :read-only="readOnly" :callback="closeChildModalCallback" :open="isFinancialSummaryOpen" :toggle-callback="setFinancialSummaryOpen" />
          </aside>

          <!-- ============ PAGE A4 : le document sélectionné ============ -->
          <QuoteDocument v-show="isQuoteSelected" :key="documentsKey + '-quote'" :read-only="readOnly" :business="business" :client-name="clientName" :client-input="clientInput" :title-input="inputs.title" :business-elements="inputs.businessElements" :totals="totals" :default-unit-price="defaultUnitPrice" :default-tva-percent="defaultTvaPercent" />
          <InvoiceDocument v-show="isInvoiceSelected" :key="documentsKey + '-invoice'" :business="business" :client-name="clientName" :default-unit-price="defaultUnitPrice" :default-tva-percent="defaultTvaPercent"/>
          <DepositDocument v-show="isDepositSelected" :key="documentsKey + '-deposit'" :business="business" :client-name="clientName" :default-unit-price="defaultUnitPrice" :default-tva-percent="defaultTvaPercent"/>
          <template v-for="(creditNote) in business.creditNotes" :key="documentsKey + '-creditNote-' + creditNote.number" >
            <CreditNoteDocument v-show="isCreditNoteTypeSelected && selectedCreditNote.number === creditNote.number"
                                :inputs="inputs.businessCreditNotes.find(e => e.number === creditNote.number)"
                                :business="business"
                                :client-name="clientName"
                                :read-only="readOnly"
                                :selected-credit-note="creditNote"
                                :default-unit-price="defaultUnitPrice"
                                :default-tva-percent="defaultTvaPercent"/>
          </template>

          <!-- ============ COLONNE DROITE : commune à tous les documents (Statut, Dossier, Acompte & signature pour le devis, informations générales, Valider) ============ -->
          <aside class="actions-sidebar">
            <StatusSection :business="business" :read-only="readOnly" :inputs="inputs.status" :document-type="selectedDocument.type" :credit-note="selectedCreditNote" :credit-note-inputs="selectedCreditNoteInputs" :callback="closeChildModalCallback" :sign-callback="openSignBusinessModal" :key="selectedCreditNote ? selectedCreditNote.number : undefined" />
            <div v-if="!readOnly" class="actions-divider"></div>
            <DepositSignatureSection v-if="isQuoteSelected && !readOnly" :key="documentsKey + '-signature'" :read-only="readOnly" :business="business" :deposit-input="inputs.deposit" :deposit-percent-input="inputs.depositPercent" :signature-input="inputs.signature" :totals="totals" />
            <template v-if="!readOnly">
              <div class="actions-divider"></div>
              <CustomButton @click="openEditGeneralInfosBusinessModal" :is-secondary="true" :isCompact="true" :params="buttons.editHeaderAndFooter" />
              <div v-if="!isDocumentReadOnly || isSelectedCreditNoteEditable" class="actions-divider"></div>
              <CustomButton v-if="!isDocumentReadOnly || isSelectedCreditNoteEditable" @click="submitForm" :params="buttons.submit" />
            </template>
          </aside>

        </div>
      </CustomForm>
      </div>
    </div>
  </CustomModal>
  <EditGeneralInfosBusinessModal ref="editGeneralInfosBusinessModal" :callback=closeChildModalCallback />
  <AddCreditNoteModal ref="addCreditNoteModal" :callback=closeChildModalCallback />
  <SignBusinessModal ref="signBusinessModal" :callback=closeChildModalCallback />
  <ClientInformationsMissingModal ref="clientInformationsMissingModal" :callback="closeModal" />
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faXmark, faPlus, faUpLong, faCheck, faExclamation, faMinus, faDownload, faLemon} from "@fortawesome/free-solid-svg-icons";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import {NotificationService} from "@vues/v-common";
import {BusinessController} from "@/controllers/business/BusinessController";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {isEditableBusiness} from "@/utils";
import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";
import AddCreditNoteModal from "@/components/modals/AddCreditNoteModal.vue";
import SignBusinessModal from "@/components/modals/SignBusinessModal.vue";
import ClientInformationsMissingModal from "@/components/modals/ClientInformationsMissingModal.vue";
import {ClientController} from "@/controllers/business/ClientController";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import EditGeneralInfosBusinessModal from "@/components/modals/EditGeneralInfosBusinessModal.vue";
import CreditNoteDocument from "@/components/businessEdition/CreditNoteDocument.vue";
import DepositDocument from "@/components/businessEdition/DepositDocument.vue";
import InvoiceDocument from "@/components/businessEdition/InvoiceDocument.vue";
import QuoteDocument from "@/components/businessEdition/QuoteDocument.vue";
import OrderNumberSection from "@/components/businessEdition/OrderNumberSection.vue";
import DepositSignatureSection from "@/components/businessEdition/DepositSignatureSection.vue";
import DocumentsNavigation from "@/components/businessEdition/DocumentsNavigation.vue";
import {wrapElements} from "@/components/businessEdition/documentElementsMapper";
import {CREDIT_NOTE_STATUS_ENUM} from "@/models/creditNoteStatusEnum";
import {DEPOSIT_STATUS_ENUM, getDepositStatus} from "@/models/depositStatusEnum";
import StatusSection from "@/components/businessEdition/status/StatusSection.vue";
import FinancialSummarySection from "@/components/businessEdition/FinancialSummarySection.vue";
import SidebarAccordion from "@/components/businessEdition/SidebarAccordion.vue";
import {BusinessService} from "@/controllers/business/BusinessService";
import {BusinessGroupParamsController} from "@/controllers/business/BusinessGroupParamsController";

library.add(faXmark);
library.add(faDownload);
library.add(faLemon);
library.add(faPlus);
library.add(faCheck);
library.add(faUpLong);
library.add(faMinus);
library.add(faExclamation);

export default {
  name: 'BusinessManagementModal',
  computed: {
    // Le document consulté attend un règlement : facture envoyée, acompte émis / envoyé, avoir émis (à rembourser)
    isAwaitingPayment() {
      if (!this.business) return false;
      if (this.isInvoiceSelected) return !!this.business.invoiceNumber && this.business.status === BUSINESS_STATUS_ENUM.SENT.value;
      if (this.isDepositSelected) {
        const status = getDepositStatus(this.business);
        return status === DEPOSIT_STATUS_ENUM.ISSUED.value || status === DEPOSIT_STATUS_ENUM.SENT.value;
      }
      if (this.isCreditNoteTypeSelected) return !!this.selectedCreditNote && this.selectedCreditNote.status === CREDIT_NOTE_STATUS_ENUM.ISSUED.value;
      return false;
    },
    downloadLabel() {
      if (this.isInvoiceSelected) return "Télécharger la facture";
      if (this.isDepositSelected) return "Télécharger l'acompte";
      if (this.isCreditNoteTypeSelected) return "Télécharger l'avoir";
      return "Télécharger le devis";
    },
    downloadButton() {
      return {
        text: this.downloadLabel,
        disabled: this.isDownloading || (this.isQuoteSelected && !this.business.number),
      };
    },
    documentsKey() {
      return this.business.id + '-' + this.reloadKey;
    },
    selectedCreditNote() {
      if (!this.business || !this.business.creditNotes) return undefined;
      return this.business.creditNotes.find(creditNote => creditNote.number === this.selectedDocument.creditNoteNumber);
    },
    selectedCreditNoteInputs() {
      if (!this.selectedCreditNote) return undefined;
      return this.inputs.businessCreditNotes.find(e => e.number === this.selectedCreditNote.number);
    },
    isSelectedCreditNoteEditable() {
      return this.isCreditNoteTypeSelected && !!this.selectedCreditNote && this.selectedCreditNote.status === CREDIT_NOTE_STATUS_ENUM.CREATION.value;
    },
    isQuoteSelected() {
      return this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value;
    },
    isInvoiceSelected() {
      return this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value;
    },
    isDepositSelected() {
      return this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value;
    },
    isCreditNoteTypeSelected() {
      return this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value;
    },
    haveBusinessOption() {
      return localStorage && localStorage.options && localStorage.options.includes('business');
    }
  },
  props: {
    callback: {type: Function, default: undefined},
    readOnly: {type: Boolean, default: false},
  },
  components: {
    AddCreditNoteModal,
    SignBusinessModal,
    ClientInformationsMissingModal,
    CustomModal,
    CustomForm,
    EditGeneralInfosBusinessModal,
    CustomButton,
    QuoteDocument,
    InvoiceDocument,
    DepositDocument,
    CreditNoteDocument,
    StatusSection,
    OrderNumberSection,
    DepositSignatureSection,
    FinancialSummarySection,
    SidebarAccordion,
    DocumentsNavigation,
  },
  data() {
    return {
      business: undefined,
      businessId: undefined,
      // Client affiché sur la page A4 ; clientInput est la liste de choix, utilisée seulement tant que le client peut être changé
      clientName: '',
      clientInput: {value: undefined, type: 'select', selectValues: [], required: true, readOnly: true},
      defaultUnitPrice: undefined,
      defaultTvaPercent: undefined,
      reloadKey: 0,
      isDownloading: false,
      isDocumentReadOnly: false,
      isFinancialSummaryOpen: false,
      totals: {
        htAmount: undefined,
        tvaAmount: undefined,
        ttcAmount: undefined,
      },
      selectedDocument: {type: BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value, creditNoteNumber: null},
      inputs: {
        title: {label: 'Titre', value: '', type: 'text', required: true},
        status: {label: 'Status', value: '', type: 'select', selectValues: [], required: true},
        orderNumber: {label: 'N° Dossier', value: '', type: 'number', required: false, readOnly: true},
        signature: {value: false, left: { label: "Non", value: false }, right: { label: "Oui", value: true }, label: "Signature"},
        deposit: {label: 'Acompte €', value: '', type: 'number', required: false},
        depositPercent: {label: '%', value: 30, type: 'number', required: false},
        businessElements: [],
        businessCreditNotes: []
      },
      buttons:{
        submit: {text: "Valider"},
        generateInvoiceDocument: {text: "Générer la facture", disabled: true},
        generateDepositDocument: {text: "Générer la facture d'acompte", disabled: true},
        createCreditNote: {text: "Créer un avoir", disabled: true},
        editHeaderAndFooter: {text: "Modifier les informations générales"},
      },
    };
  },
  async mounted() {
    if(this.haveBusinessOption) {
      const resp = await BusinessGroupParamsController.get(localStorage.groupId);
      if (!resp.errors) {
        this.defaultUnitPrice = resp.defaultUnitPrice;
        this.defaultTvaPercent = resp.defaultTva;
      }
    }
  },
  watch: {
    // À l'arrivée sur un document en attente de règlement, la situation financière s'ouvre
    selectedDocument() {
      this.isFinancialSummaryOpen = this.isAwaitingPayment;
    },
  },
  methods: {
    setFinancialSummaryOpen(value) {
      this.isFinancialSummaryOpen = value;
    },
    // document : {type, creditNoteNumber} du document à afficher en premier (le devis par défaut, ou si ce document n'existe pas)
    openModal(business, document) {
      this.$refs.modal.openModal();
      this.businessId = business.id;
      this.business = business;
      this.reloadKey++;
      this.selectedDocument = document && this.isDocumentAvailable(business, document)
          ? document
          : {type: BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value, creditNoteNumber: null};
      this.buttons.generateInvoiceDocument.disabled = true;
      this.buttons.generateDepositDocument.disabled = true;
      this.inputs.title.value = business.title;
      this.inputs.businessElements = wrapElements(this.business.elements);

      // Fill creditNote
      this.inputs.businessCreditNotes = (business.creditNotes || []).map(creditNote => ({
        number: creditNote.number,
        title: {label: 'Titre', value: creditNote.body.title, type: 'text', required: true, readOnly: (this.readOnly || creditNote.status !== CREDIT_NOTE_STATUS_ENUM.CREATION.value)},
        status: {label: 'Status', value: creditNote.status, type: 'select', selectValues: [], required: true},
        totals: {
          tvaAmount: undefined,
          htAmount: undefined,
          ttcAmount: undefined,
        },
        elements: wrapElements(creditNote.body.elements, (this.readOnly || creditNote.status !== CREDIT_NOTE_STATUS_ENUM.CREATION.value))
      }));

      // Activated the button only on done quote
      if (business.status === BUSINESS_STATUS_ENUM.DONE.value) {
        this.buttons.generateInvoiceDocument.disabled = false;
      }

      // Activated the button only on done quote
      const isInvoice = !!business.invoiceNumber;
      const isQuote = !isInvoice;
      // (impossible une fois la facture générée)
      if (!(business.depositNumber) && isQuote && (
          business.status === BUSINESS_STATUS_ENUM.DONE.value ||
          business.status === BUSINESS_STATUS_ENUM.ACCEPTED.value ||
          business.status === BUSINESS_STATUS_ENUM.IN_PROGRESS.value
      )) {
        if (business.deposit && business.deposit > 0) {
          this.buttons.generateDepositDocument.disabled = false;
        }
      }

      // Activated the button credit note
      this.buttons.createCreditNote.disabled = !isInvoice;

      // Change text button of order edit
      if (!business.orderNumber) {
        this.inputs.orderNumber.value = '';
      } else {
        this.inputs.orderNumber.value = business.orderNumber;
      }

      if(this.readOnly || !isEditableBusiness(business)) {
        this.buttons.editHeaderAndFooter.text = "Consulter les informations générales";
      } else {
        this.buttons.editHeaderAndFooter.text = "Modifier les informations générales";
      }

      this.setReadOnly(this.readOnly || !isEditableBusiness(business));
      this.loadClient(business);
    },
    // Le nom du client vient de la fiche actuelle ; la liste des clients n'est chargée que si le client peut encore être changé (devis en rédaction)
    async loadClient(business) {
      const isClientEditable = !this.readOnly && isEditableBusiness(business) && !business.amendmentOfBusinessId;
      this.clientName = business.currentClientName || business.clientStructuredInformation?.name || '';
      this.clientInput.value = business.clientId;
      this.clientInput.readOnly = !isClientEditable;
      this.clientInput.selectValues = [];

      if (isClientEditable) {
        const resp = await ClientController.getAll();
        // Une autre ouverture a pu avoir lieu pendant la requête
        if (!resp.errors && this.businessId === business.id) {
          resp.sort((a, b) => a.structuredInformations.name.localeCompare(b.structuredInformations.name));
          this.clientInput.selectValues = resp.map(client => ({label: client.structuredInformations.name, value: client.id}));
        }
      } else if (!this.clientName && business.clientId) {
        const resp = await ClientController.get(business.clientId);
        if (!resp.errors && this.businessId === business.id) {
          this.clientName = resp.structuredInformations?.name || '';
        }
      }
    },
    closeModal() {
      this.$refs.modal?.closeModal();
    },
    terminate() {
      this.closeModal();
      this.callback();
    },
    isDocumentAvailable(business, document) {
      switch (document.type) {
        case BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value: return true;
        case BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value: return !!business.invoiceNumber;
        case BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value: return !!business.depositNumber;
        case BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value:
          return (business.creditNotes || []).some(creditNote => creditNote.number === document.creditNoteNumber);
        default: return false;
      }
    },
    closeChildModalCallback() {
      // Le rechargement remet l'onglet Devis par défaut : on garde le document consulté s'il existe toujours
      const previousDocument = this.selectedDocument;
      BusinessController.get(this.businessId).then(resp => {
        this.openModal(resp);
        if (this.isDocumentAvailable(resp, previousDocument)) this.selectedDocument = previousDocument;
      });
      this.callback();
    },
    async downloadSelectedDocument() {
      if (this.isDownloading) return;
      this.isDownloading = true;
      try {
        const type = this.isInvoiceSelected ? 'invoice' : this.isDepositSelected ? 'deposit' : this.isCreditNoteTypeSelected ? 'creditNote' : 'quote';
        await BusinessService.downloadPdf(this.business, type, this.selectedDocument.creditNoteNumber);
      } finally {
        this.isDownloading = false;
      }
    },
    openEditGeneralInfosBusinessModal() {
      this.$refs.editGeneralInfosBusinessModal.openModal(this.business);
    },
    openSignBusinessModal() {
      this.$refs.signBusinessModal.openModal(this.business);
    },
    setReadOnly(readOnly) {
      this.isDocumentReadOnly = readOnly;
      this.inputs.title.readOnly = readOnly;
      this.inputs.deposit.readOnly = readOnly;
      this.inputs.depositPercent.readOnly = readOnly;
      this.inputs.signature.readOnly = readOnly;
      for (let i = 0; this.inputs.businessElements.length > i; i++) {
        this.inputs.businessElements[i].text.readOnly = readOnly;
        this.inputs.businessElements[i].durationQuantity.readOnly = readOnly;
        this.inputs.businessElements[i].unitPrice.readOnly = readOnly;
        this.inputs.businessElements[i].discountedRate.readOnly = readOnly;
        this.inputs.businessElements[i].tva.readOnly = readOnly;
        this.inputs.businessElements[i].amount.readOnly = readOnly;
      }
    },
    async submitForm() {
      let body;

      // Cas facture / quote / accompte
      if (!isEditableBusiness(this.business)) {
        body = {
          status: this.inputs.status.value
        };
      } else {
        body = {
          title: this.inputs.title.value,
          // eslint-disable-next-line no-unused-vars
          elements: Object.entries(this.inputs.businessElements).map(([key, value]) => ({
            type: value.type,
            text: value.text.value,
            durationQuantity: value.durationQuantity.value,
            unitPrice: value.unitPrice.value,
            discountedRate: value.discountedRate.value,
            tva: value.tva.value,
            amount: value.amount.value
          })),
          tvaAmount: this.totals.tvaAmount,
          htAmount: this.totals.htAmount,
          ttcAmount: this.totals.ttcAmount,
          signature: this.inputs.signature.value,
          deposit: this.inputs.deposit.value,
          status: this.inputs.status.value,
        }

        // Client changé depuis la page du devis
        if (this.clientInput.value && this.clientInput.value !== this.business.clientId) {
          body.clientId = this.clientInput.value;
        }
      }

      // Cas Avoir
      for (const i in this.business.creditNotes) {
        const creditNote = this.business.creditNotes[i];
        if (creditNote.status === CREDIT_NOTE_STATUS_ENUM.CREATION.value) {
          if (body.creditNotes === undefined) {
            body.creditNotes = [];
          }

          const creditNoteInputs = this.inputs.businessCreditNotes.find(cn => cn.number === creditNote.number);

          body.creditNotes.push({
            number: creditNote.number,
            status: creditNoteInputs.status.value,
            body: {
              title: creditNoteInputs.title.value,
              // eslint-disable-next-line no-unused-vars
              elements: Object.entries(creditNoteInputs.elements).map(([key, value]) => ({
                type: value.type,
                text: value.text.value,
                durationQuantity: value.durationQuantity.value,
                unitPrice: value.unitPrice.value,
                discountedRate: value.discountedRate.value,
                tva: value.tva.value,
                amount: value.amount.value
              })),
              tvaAmount: creditNoteInputs.totals.tvaAmount,
              htAmount: creditNoteInputs.totals.htAmount,
              ttcAmount: creditNoteInputs.totals.ttcAmount,
            }
          });
        }
      }

      const res = await this.$refs.form.submitForm(() => BusinessController.patch(this.businessId, body));

      if (!res.errors) {
        (new NotificationService).addNotification("success", "Contrat modifié");
        this.closeChildModalCallback();
      }
    },
    async generateInvoiceDocument() {
      const res = await this.$refs.generateInvoiceDocumentForm.submitForm(() => BusinessController.generateInvoiceDocument(this.businessId));
      if (!res.errors) {
        (new NotificationService).addNotification("success", "Facture générée");
        this.closeChildModalCallback();
      } else {
        // Fiche du client incomplète : on propose d'aller la compléter
        const clientError = res.errors.find(error => error.code === 'CLIENT_ADDRESS_MISSING' || error.code === 'CLIENT_INFORMATIONS_MISSING');
        if (clientError) {
          this.$refs.clientInformationsMissingModal.openModal(this.business.clientId, clientError);
        }
      }
    },
    async generateDepositDocument() {
      const res = await this.$refs.generateDepositDocumentForm.submitForm(() => BusinessController.generateDepositDocument(this.businessId));
      if (!res.errors) {
        (new NotificationService).addNotification("success", "Acompte générée");
        this.closeChildModalCallback();
      }
    }
  }
}

</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.document-workspace
  display flex
  flex-direction column

.document-editor-button
  &.full-width
    width 100%
    margin-bottom 10px

    &:last-child
      margin-bottom 0

.separator
  margin-top 15px
  height 2px
  background-color THEME_COLOR_PRIMARY_FADDED
  opacity 0.6

.workspace-grid
  display flex
  align-items flex-start
  gap 20px

/* ---------- Corps de la modale : le liseré jaune prolonge l'onglet actif sur toute la largeur ---------- */
.workspace-body
  padding 16px 30px 0
  border-top 4px solid PAGE_LEMON
  border-radius 10px 10px 0 0

/* ---------- Sidebar navigation documents ---------- */
.documents-sidebar
  flex 0 0 210px
  max-height calc(92vh - 150px)
  overflow-y auto
  padding 16px 12px
  background-color #ffffff
  border 1px solid #c9d0d9
  border-radius 10px
  box-shadow 0 1px 3px rgba(20, 25, 35, 0.12)
  align-self flex-start
  position sticky
  top 0
  z-index 2

.download-container
  margin-top 0

// Bouton de téléchargement : contour et texte de la couleur principale, plus haut qu'un bouton compact, sans fond plein pour rester discret
.download-button
  display flex
  align-items center
  justify-content center
  gap 9px
  width 100%
  min-height 44px
  padding 8px 12px
  border 1.5px solid THEME_COLOR_PRIMARY
  border-radius 10px
  background-color #ffffff
  color THEME_COLOR_PRIMARY
  font-family inherit
  font-size 14px
  font-weight 700
  cursor pointer
  transition background-color .15s ease, box-shadow .15s ease

  svg
    font-size 16px

  &:hover:not(:disabled)
    background-color PAGE_LEMON_HOVER
    box-shadow 0 4px 10px -6px rgba(20, 25, 35, 0.45)

  &:disabled
    border-color #b5bfcc
    color #6a717d
    cursor not-allowed

.sidebar-divider
  height 1px
  background-color #dde1e7
  margin 14px 0 10px

.generate-buttons-container
  padding-top 5px
  display flex
  flex-direction column
  gap 10px

.sidebar-group-label
  margin 0 0 4px
  font-size 11.5px
  font-weight 800
  letter-spacing 0.05em
  text-transform uppercase
  color #3a4250

/* ---------- Sidebar gestion / actions ---------- */
.actions-sidebar
  display flex
  justify-content space-between
  flex-direction column
  width 250px
  padding 16px
  background-color #ffffff
  border 1px solid #c9d0d9
  border-radius 10px
  box-shadow 0 1px 3px rgba(20, 25, 35, 0.12)

.actions-divider
  height 1px
  background-color #dde1e7
  margin 12px 0
</style>

<style lang="stylus">
.business-management-modal
  panel-max-height = calc(92vh - 150px)

  // Plus de titre : le bouton de fermeture se place à droite, sur la ligne des onglets qui dépasse au-dessus de la modale.
  // Sélecteurs enfant (>) obligatoires : les modales ouvertes depuis celle-ci (ForceStatusModal...) sont DANS ce bloc
  // et ne doivent pas hériter de ces règles.
  > .modal > .header-container
    position absolute
    bottom 100%
    right 24px
    button
      display grid
      place-items center
      width 52px
      height 36px
      padding 0
      border 0
      border-radius 10px 10px 0 0
      // Onglet citron (même jaune que le document ouvert) avec une croix bleu nuit : fort contraste sur le fond gris de la page
      background-color #FFDD2E
      color #24396b
      font-size 18px
      cursor pointer
      transition background-color .15s ease
      &:hover, &:focus-visible
        background-color #c2381f
        color #ffffff

  // Les onglets et le corps gèrent eux-mêmes leurs marges, pour que le liseré jaune soit pleine largeur
  > .modal > .content
    padding-left 0 !important
    padding-right 0 !important

  > .modal
    position relative
    // La marge du haut laisse la place aux onglets, positionnés au-dessus de la modale
    margin-top 40px
    width min(96vw, 1220px) !important
    max-height calc(92vh - 40px)
    background-color #e3e7ec

  // Libellés des champs des colonnes latérales : plus contrastés que le gris par défaut
  .documents-sidebar label, .actions-sidebar label
    color #2f3744 !important
</style>